import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { maskDecimal } from '@givebutter/util';
import { faDownload } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { exportIndividualPayout, exportPayouts, fetchPayouts } from 'api/payouts';
import * as ExportDataDialog from 'assets/js/components/ExportDataDialog';
import * as DropdownMenu from 'components/dropdown-menu';
import * as Table from 'components/table';
import { useReactTableState } from 'components/table';
import { TruncatedText } from 'components/truncated-text';
import { EmptyData } from 'components/util';
import { useCurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { dateFromServer, rawDateFromServer } from 'helpers/dates';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { capitalize } from 'lodash';
import { accountQueryKeys } from 'queries/account-query-keys';
import { Payout } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

const columnHelper = createColumnHelper<Payout>();

const PayoutsHistory = () => {
    const { instantPayouts = false } = useFlags();
    const currencySymbol = useCurrencySymbol();
    const { accountId } = useCurrentAccount();
    const { success, error } = useToast();
    const { pagination, setPagination } = useReactTableState();
    const params = { page: pagination.pageIndex + 1 };
    const { data } = useSuspenseQuery({
        queryKey: accountQueryKeys.payoutsWithFilters(accountId, params),
        queryFn: () => fetchPayouts(accountId, params),
    });
    const { state: locationState } = useLocation<{ openExportDialog?: boolean }>();

    const { mutate: exportIndividual } = useMutation({
        mutationFn: (id: number) => exportIndividualPayout(accountId, id),
        onSuccess() {
            success(`Success! We'll email you when the report is ready to download.`);
        },
        onError(e: GivebutterAxiosError) {
            error(e.response?.data?.message ?? 'Whoops. Something went wrong. Please try again.');
        },
    });

    const { mutateAsync: exportAllPayouts } = useMutation({
        mutationFn: (accountId: number) => exportPayouts(accountId),
    });

    const columns = useMemo(
        () => [
            columnHelper.accessor('number', { header: 'Ref #' }),
            columnHelper.accessor('campaign', {
                header: 'Campaign',
                cell: info => {
                    const campaign = info.getValue();

                    return (
                        <div className={'flex max-w-56'}>
                            <TruncatedText>
                                <a
                                    className="font-semibold"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    href={campaign.url}
                                >
                                    {campaign.title}
                                </a>
                            </TruncatedText>
                        </div>
                    );
                },
                meta: {
                    ignoreLink: true,
                },
            }),
            columnHelper.accessor('method', {
                header: 'Method',
                cell: info =>
                    `${capitalize(info.getValue())} ${info.row.original.last4 ? '*' + info.row.original.last4 : ''}`,
            }),
            columnHelper.accessor('status_friendly', { header: 'Status' }),
            columnHelper.accessor('amount', {
                header: 'Amount',
                cell: info => `${currencySymbol}${maskDecimal(info.getValue())}`,
            }),
            ...(instantPayouts
                ? [
                      columnHelper.accessor('fee', {
                          header: 'Fee',
                          cell: info => `${currencySymbol}${maskDecimal(info.getValue())}`,
                      }),
                  ]
                : []),
            columnHelper.accessor('tip', {
                header: 'Tip',
                cell: info => `${currencySymbol}${maskDecimal(info.getValue())}`,
            }),
            columnHelper.accessor('created_at', {
                header: 'Initiated On',
                cell: info => (
                    <p className="font-medium">
                        {dateFromServer(info.getValue()).toFormat('LLL dd yyyy')}
                    </p>
                ),
            }),
            columnHelper.accessor('expected_at', {
                header: 'Est. Arrival',
                cell: info => {
                    const estDate = info.getValue();

                    if (!estDate) {
                        return <EmptyData />;
                    }

                    const parsedDate =
                        info.row.original.type === 'instant'
                            ? dateFromServer(estDate)
                            : rawDateFromServer(estDate);

                    return <p className="font-medium">{parsedDate.toFormat('LLL dd yyyy')}</p>;
                },
            }),
            columnHelper.display({
                id: Table.Constants.Actions,
                cell: info => {
                    const history = info.row.original;

                    if (!history.has_report) {
                        return null;
                    }

                    return (
                        <DropdownMenu.Root>
                            <Table.TableRowDropdownTrigger />
                            <DropdownMenu.Content sideOffset={5} align={'end'}>
                                <DropdownMenu.Item
                                    onSelect={e => e.stopPropagation()}
                                    onClick={() => {
                                        exportIndividual(history.id);
                                    }}
                                >
                                    Export Payout
                                </DropdownMenu.Item>
                            </DropdownMenu.Content>
                        </DropdownMenu.Root>
                    );
                },
            }),
        ],
        [currencySymbol, exportIndividual, instantPayouts],
    );

    const table = useReactTable({
        data: data?.data ?? [],
        columns,
        pageCount: data?.meta?.last_page ?? -1,
        getCoreRowModel: getCoreRowModel(),
        state: {
            pagination,
        },
        onPaginationChange: setPagination,
        enableSorting: false,
        manualPagination: true,
        enableHiding: false,
        enableColumnPinning: false,
    });

    return (
        <div className={'flex flex-col gap-4'}>
            <div className={'flex justify-end'}>
                <ExportDataDialog.Root
                    label="Payouts"
                    onSubmit={() => exportAllPayouts(accountId)}
                    defaultOpen={locationState?.openExportDialog}
                >
                    <ExportDataDialog.Trigger asChild>
                        <Button
                            size={'sm'}
                            className="flex items-center gap-2 whitespace-nowrap"
                            variant="outline"
                            color="neutral"
                        >
                            <FontAwesomeIcon icon={faDownload} />
                            <p>Export</p>
                        </Button>
                    </ExportDataDialog.Trigger>
                </ExportDataDialog.Root>
            </div>
            <Table.Root variant={'bordered'} table={table}>
                <div className="flex flex-col gap-y-4">
                    <Box className={'overflow-x-auto'}>
                        <Table.Content>
                            <Table.Header />
                            <Table.Body />
                        </Table.Content>
                        <Table.Empty variant={'primary'}>
                            <Table.EmptyTitle>No payouts found</Table.EmptyTitle>
                        </Table.Empty>
                    </Box>
                    <Table.Pagination>
                        <Table.PaginationPageNavigation />
                    </Table.Pagination>
                </div>
            </Table.Root>
        </div>
    );
};

export { PayoutsHistory };
